import { ArticlesFeedPage, FeedArticle } from '../types';
import { NormalizedFeedRequest } from './feedRequest';

// COM-638: pure fixture-shaping logic for the `getArticlesFeed` mock
// (mockSocket.ts). Extracted so the dedup/sort/pagination rules the e2e
// suite implicitly relies on can be unit-tested directly, instead of only
// being exercised indirectly through the UI - a wrong mock here would
// quietly rot every All Articles page test.
//
// Mirrors `DatabaseService#getArticlesFeed` (backend/src/services/dbService.ts)
// row-for-row:
//   - one raw row per (article, source) pair, i.e. an article shared by two
//     sources appears twice before dedup;
//   - dedup picks the row with the latest `sortDate` per url, tie-broken by
//     the lowest `sourceId` (the SQL's `DISTINCT ON (a.url) ... ORDER BY
//     a.url, "sortDate" DESC, sa.source_id ASC`);
//   - `sortDate` is `GREATEST(createdAt, COALESCE(updateDate, createdAt))`;
//   - the default sort is by `sortDate` desc with a `url` asc tiebreak
//     (always ascending, regardless of the requested direction - matches
//     `ORDER BY <sortExpr> <sortDir>, url ASC`);
//   - title sort compares `LOWER(title)` (case-insensitive);
//   - search is a case-insensitive title substring match (`ILIKE '%..%'`
//     with `%`/`_` treated as literal characters, since the mock never
//     builds a LIKE pattern out of the search string).

// One raw (article, source) row, as it would come out of the `source_articles`
// / `articles` / `sources` join before `DISTINCT ON (a.url)` collapses it.
// `createdAt` only exists to compute `sortDate` - it never appears on the
// `FeedArticle` shape returned to the frontend.
export interface FeedFixtureRow {
  url: string;
  title: string;
  createdAt: string;
  updateDate: string | null;
  relevance: string;
  isNew: boolean;
  sourceId: string;
  sourceName: string;
  sourceUrl: string;
}

// GREATEST(a.created_at, COALESCE(a.update_date, a.created_at)) - ISO-8601
// UTC strings of matching length compare correctly with plain string
// comparison, so there's no need to parse into Date objects.
export function computeSortDate(row: FeedFixtureRow): string {
  const candidate = row.updateDate ?? row.createdAt;
  return candidate > row.createdAt ? candidate : row.createdAt;
}

// COM-639: mirrors backend `buildFeedWhereClause`'s
// `AND sa.source_id = ANY(${sourceIds})` clause. Must run BEFORE
// `dedupeFeedRows` - see that function's comment and `buildArticlesFeedPage`
// below for why order matters here (the attribution rule).
//
// `null` = no filter (every row passes). `[]` is the user's deliberate
// "deselect every source" signal - `Array.prototype.includes` on an empty
// `Set` matches nothing, so it naturally yields zero rows without any
// special-casing, mirroring `= ANY('{}'::text[])` in the real SQL.
export function filterFeedRowsBySourceIds(rows: FeedFixtureRow[], sourceIds: string[] | null): FeedFixtureRow[] {
  if (sourceIds === null) {
    return rows;
  }

  const allowed = new Set(sourceIds);
  return rows.filter((row) => allowed.has(row.sourceId));
}

// `YYYY-MM-DD` -> that day's midnight UTC, as an ISO string comparable to
// `computeSortDate`'s output (both are ISO-8601 UTC strings of matching
// length - see `compareStrings` below for why plain string comparison is
// valid here).
function startOfUtcDay(dateStr: string): string {
  return `${dateStr}T00:00:00.000Z`;
}

// `YYYY-MM-DD` -> midnight UTC of the FOLLOWING day, i.e. the exclusive
// upper bound for "the whole calendar day of `dateStr`, inclusive". Built
// via `Date.UTC` (which correctly rolls over month/year boundaries, e.g.
// 2026-08-31 -> 2026-09-01) rather than string arithmetic.
function startOfNextUtcDay(dateStr: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString();
}

// COM-639: mirrors backend `buildFeedWhereClause`'s date-bound clauses -
// compares `computeSortDate` (`GREATEST(a.created_at,
// COALESCE(a.update_date, a.created_at))`, the same key the feed sorts by),
// inclusive of the WHOLE calendar day at both ends, in UTC. Must run BEFORE
// `dedupeFeedRows`, same as `filterFeedRowsBySourceIds` above and for the
// same reason (see `buildArticlesFeedPage`).
export function filterFeedRowsByDateRange(
  rows: FeedFixtureRow[],
  dateFrom: string | null,
  dateTo: string | null
): FeedFixtureRow[] {
  if (dateFrom === null && dateTo === null) {
    return rows;
  }

  const lowerBound = dateFrom !== null ? startOfUtcDay(dateFrom) : null;
  const upperBound = dateTo !== null ? startOfNextUtcDay(dateTo) : null;

  return rows.filter((row) => {
    const sortDate = computeSortDate(row);
    if (lowerBound !== null && sortDate < lowerBound) {
      return false;
    }
    if (upperBound !== null && sortDate >= upperBound) {
      return false;
    }
    return true;
  });
}

// `DISTINCT ON (a.url)` with `ORDER BY a.url, "sortDate" DESC, sa.source_id
// ASC`: keeps, per url, the row with the latest sortDate, and breaks ties
// deterministically by the lowest sourceId (never "whichever came first" -
// that would flap depending on fixture array order).
export function dedupeFeedRows(rows: FeedFixtureRow[]): FeedFixtureRow[] {
  const winners = new Map<string, FeedFixtureRow>();

  for (const row of rows) {
    const existing = winners.get(row.url);
    if (!existing) {
      winners.set(row.url, row);
      continue;
    }

    const rowSortDate = computeSortDate(row);
    const existingSortDate = computeSortDate(existing);

    if (
      rowSortDate > existingSortDate ||
      (rowSortDate === existingSortDate && row.sourceId < existing.sourceId)
    ) {
      winners.set(row.url, row);
    }
  }

  return Array.from(winners.values());
}

// `a.title ILIKE '%' || search || '%'` on already-escaped input: a plain
// case-insensitive substring match where `%`/`_` are literal characters, not
// wildcards (the mock never builds a real LIKE pattern, so there's nothing
// to escape).
export function filterFeedRowsBySearch(rows: FeedFixtureRow[], search?: string | null): FeedFixtureRow[] {
  const trimmed = search?.trim();
  if (!trimmed) {
    return rows;
  }

  const needle = trimmed.toLowerCase();
  return rows.filter((row) => row.title.toLowerCase().includes(needle));
}

// Plain byte-order comparison - NOT `localeCompare`, which is locale-aware
// (accent/case folding, "collation" rules that vary by environment). The
// real Postgres runs `C` collation (see dbService.ts's `FEED_SORT_EXPRESSIONS`
// comment), i.e. a straight byte-order compare, so matching that here means
// not depending on `localeCompare`'s behaviour lining up by fixture luck.
function compareStrings(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

// `ORDER BY <sortExpr> <sortDir>, url ASC` - the url tiebreak direction is
// always ascending, independent of the requested sortDir.
export function sortFeedRows(
  rows: FeedFixtureRow[],
  sortBy: 'date' | 'title',
  sortDir: 'asc' | 'desc'
): FeedFixtureRow[] {
  const dirMultiplier = sortDir === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const primary =
      sortBy === 'title'
        ? compareStrings(a.title.toLowerCase(), b.title.toLowerCase())
        : compareStrings(computeSortDate(a), computeSortDate(b));

    if (primary !== 0) {
      return primary * dirMultiplier;
    }

    return compareStrings(a.url, b.url);
  });
}

export function paginateFeedRows(rows: FeedFixtureRow[], limit: number, offset: number): FeedFixtureRow[] {
  return rows.slice(offset, offset + limit);
}

function toFeedArticle(row: FeedFixtureRow): FeedArticle {
  return {
    url: row.url,
    title: row.title,
    updateDate: row.updateDate,
    relevance: row.relevance,
    isNew: row.isNew,
    sourceId: row.sourceId,
    sourceName: row.sourceName,
    sourceUrl: row.sourceUrl,
  };
}

// The full pipeline the mock's `getArticlesFeed` case runs: sourceIds filter
// -> date filter -> dedup -> search filter -> sort -> paginate, with `total`
// counting the deduped+filtered set (matching the backend's separate
// `COUNT(DISTINCT a.url)` query, which shares the same WHERE clause).
//
// COM-639: `sourceIds`/date filtering runs BEFORE `dedupeFeedRows` -
// mirroring `dbService.ts`'s `buildFeedWhereClause`, whose WHERE clause
// applies inside the `DISTINCT ON (a.url)` subquery, ahead of dedup. This is
// the attribution rule: a url shared by two sources where the unfiltered
// dedup winner gets excluded by `sourceIds` must still surface, now
// attributed to whichever allowed source ingested it - not disappear
// because dedup (run first) would have already discarded its only surviving
// row. Filtering the deduped set instead would silently drop that url
// whenever the filter excludes the dedup winner.
//
// `filterFeedRowsBySearch` stays AFTER dedup (unlike the SQL, where it's in
// the same pre-dedup WHERE clause): search matches on `title`, which is
// identical across every row sharing a url (title lives on the `articles`
// row, not the per-source join row), so pre- vs post-dedup search filtering
// is observably equivalent here - not true of `sourceIds`/dates, which vary
// per source/join row and therefore change WHICH row dedup would keep.
//
// `request` must already be normalized (`normalizeFeedRequest` from
// `./feedRequest`) - this function trusts `limit`/`offset` to be in range
// and does not re-clamp them itself, so an un-normalized negative offset
// would still leak `Array.prototype.slice`'s "count from the end" behaviour.
//
// COM-640: deliberately returns `ArticlesFeedPage` MINUS `lastUpdatedAt` -
// that field is a project-wide aggregate over ALL rows (immune to this
// function's own sourceIds/date/search filtering), computed independently
// by `mockSocket.ts`'s `getArticlesFeed` case and merged in there, the same
// separation the real backend keeps between `SourcesService#getArticlesFeed`
// and `getLastCheckedAt` (see backend/src/index.ts's handler).
export function buildArticlesFeedPage(
  rows: FeedFixtureRow[],
  request: NormalizedFeedRequest
): Omit<ArticlesFeedPage, 'lastUpdatedAt'> {
  const bySource = filterFeedRowsBySourceIds(rows, request.sourceIds);
  const byDate = filterFeedRowsByDateRange(bySource, request.dateFrom, request.dateTo);
  const deduped = dedupeFeedRows(byDate);
  const filtered = filterFeedRowsBySearch(deduped, request.search);
  const sorted = sortFeedRows(filtered, request.sortBy, request.sortDir);
  const page = paginateFeedRows(sorted, request.limit, request.offset);

  return {
    articles: page.map(toFeedArticle),
    total: filtered.length,
    offset: request.offset,
  };
}
